import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom";
import { Suspense, lazy } from "react";
import ProtectedRoute from "./ProtectedRoute";
import SelectedGatewayLayout from "./SelectedGatewayLayout";
import SignalRTestRouteGuard from "./SignalRTestRouteGuard";
import LoadingScreen from "components/shared/LoadingScreen";
import t from "utils/i18n";

// Lazy-loaded components
const Home = lazy(() => import("pages/Home/Home"));
const Breakers = lazy(() => import("pages/Breakers/Breakers"));
const BreakerDashboard = lazy(
  () => import("pages/BreakerDashboard/BreakerDashboard"),
);
const Schedule = lazy(() => import("pages/Schedule/Schedule"));
const Activity = lazy(() => import("pages/Activity/Activity"));
const Settings = lazy(() => import("pages/Settings/Settings"));
const UserAccount = lazy(() => import("pages/Settings/components/UserAccount"));
const ManageGateways = lazy(
  () => import("pages/Settings/components/ManageGateways"),
);
const Support = lazy(() => import("pages/Settings/components/Support"));
const OtherProducts = lazy(
  () => import("pages/Settings/components/OtherProducts"),
);
const AboutInhab = lazy(() => import("pages/Settings/components/AboutInhab"));
const NotFound = lazy(() => import("pages/NotFound/NotFound"));
const Login = lazy(() => import("pages/Login/Login"));
const ResetPasswordCallback = lazy(() => import("pages/ResetPasswordCallback"));
const SignalRTest = lazy(() => import("pages/SignalRTest"));

const suspenseWrapper = (element: JSX.Element) => (
  <Suspense
    fallback={
      <div>
        <LoadingScreen>{t("suspense.loading")}</LoadingScreen>
      </div>
    }
  >
    {element}
  </Suspense>
);

const AppRoute = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Protected routes with selected gateway check */}
      <Route element={<ProtectedRoute />}>
        <Route element={<SelectedGatewayLayout />}>
          <Route index element={suspenseWrapper(<Home />)} />
          <Route path="/breakers" element={suspenseWrapper(<Breakers />)} />
          <Route
            path="/breakers/:gatewayId/:loadCentreSlotId"
            element={suspenseWrapper(<BreakerDashboard />)}
          />
          <Route path="/schedule" element={suspenseWrapper(<Schedule />)} />
          <Route path="/activity" element={suspenseWrapper(<Activity />)} />
          <Route path="*" element={suspenseWrapper(<NotFound />)} />
        </Route>

        {/* Settings routes — accessible even without a gateway */}
        <Route path="/settings" element={suspenseWrapper(<Settings />)}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={suspenseWrapper(<UserAccount />)} />
          <Route
            path="manage-gateways"
            element={suspenseWrapper(<ManageGateways />)}
          />
          <Route path="about" element={suspenseWrapper(<AboutInhab />)} />
          <Route path="products" element={suspenseWrapper(<OtherProducts />)} />
          <Route path="support" element={suspenseWrapper(<Support />)} />
        </Route>

        <Route
          path="/signalr-test"
          element={suspenseWrapper(
            <SignalRTestRouteGuard>
              <SignalRTest />
            </SignalRTestRouteGuard>,
          )}
        />
      </Route>

      {/* Public routes */}
      <Route
        path="/login"
        element={suspenseWrapper(
          <div id="login">
            <Login />
          </div>,
        )}
      />
      <Route
        path="/reset-password-callback"
        element={suspenseWrapper(<ResetPasswordCallback />)}
      />
    </>,
  ),
);

export default AppRoute;
